import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useSocketEvent} from 'socket.io-react-hook';

import {useProgressToast} from 'components';
import {UserContext} from 'context';
import {SocketEvent} from 'enums';

import {useSocketEventCallback} from './socket-event-callback';

type Config<TData> = {
    notification?: {
        enabled: boolean;
        labels?: {
            pending?: string;
            success?: string;
            error?: string;
        }
    },
    onComplete: (data: TData) => void,
    onError?: (error) => void,
    onToggleLoading?: (loading: boolean) => void,
    onProgress?: (progress: number) => void,
}

class TaskRequest<TRequestData> {
    private _inProgress: boolean;
    private _taskId: string;
    private readonly _fetchData: (socketId: string, data: TRequestData) => Promise<{taskId: string}>;
    private readonly _onRequestError: (e) => void;

    constructor(fetchData: (socketId: string, data: TRequestData) => Promise<{taskId: string}>, onRequestError) {
        this._fetchData = fetchData;
        this._onRequestError = onRequestError;
    }

    get taskId() {
        return this._taskId;
    }

    get inProgress() {
        return this._inProgress;
    }

    complete() {
        this._inProgress = false;
    }

    execute = async (socketId: string, data: TRequestData) => {
        try {
            this._inProgress = true;
            const responseData = await this._fetchData(socketId, data);
            this._taskId = responseData.taskId;
        } catch (e) {
            this.complete();
            await this._onRequestError?.(e);
        }
    };
}

enum TaskCompletionStatus {
    Success = 'success',
    Failed = 'failed'
}

type TaskCompletionResult<TData> = {
    status: TaskCompletionStatus.Success,
    data: TData;
} | {
    status: TaskCompletionStatus.Failed;
    error: any;
};

export function useTask<TRequestData, TData>(
    fetchData: (socketId: string, requestData: TRequestData) => Promise<{taskId: string}>,
    {notification = {enabled: false}, onComplete, onToggleLoading, onProgress, onError}: Config<TData>
) {
    const {socket} = useContext(UserContext);
    const [taskRequest, setTaskRequest] = useState<TaskRequest<TRequestData>>();
    const [stopTaskSignal, setStopTaskSignal] = useState(false);
    const {enabled: notificationEnabled, labels} = useMemo(() => notification, [notification]);
    const handleLoadingClose = useCallback(() => {
        setStopTaskSignal(true);
    }, []);

    const {notifyProgressStart, updateProgress, notifyError, notifySuccess} = useProgressToast({
        labels,
        options: {onClick: handleLoadingClose}
    });

    const [taskCompletionResult, setTaskCompletionResult] = useState<Record<string, TaskCompletionResult<TData>>>();

    const onTaskCompleted = useCallback(({taskId, data}) => {
        notifySuccess();
        setTaskCompletionResult({[taskId]: {status: TaskCompletionStatus.Success, data}});
    }, [notifySuccess, setTaskCompletionResult]);

    const onRequestError = useCallback(({error}) => {
        onToggleLoading?.(false);
        onError?.(error);
        notifyError();
    }, [onToggleLoading, onError, notifyError]);

    const onTaskFailed = useCallback(({taskId, error}) => {
        setTaskCompletionResult({[taskId]: {status: TaskCompletionStatus.Failed, error}});
    }, [setTaskCompletionResult]);

    const onTaskProgress = useCallback(({taskId, progress}) => {
        if (taskRequest?.taskId === taskId) {
            onProgress?.(progress);
            updateProgress(progress);
        }
    }, [onProgress, taskRequest, updateProgress]);

    const {sendMessage: sendCancelTaskFn} = useSocketEvent(socket, SocketEvent.CancelTask);
    const sendCancelTaskRef = useRef<any>();
    const onRequestErrorRef = useRef<any>();

    useEffect(() => {
        if (onRequestError && !onRequestErrorRef.current) {
            onRequestErrorRef.current = onRequestError;
        }
    }, [onRequestErrorRef, onRequestError]);

    useEffect(() => {
        if (sendCancelTaskFn && !sendCancelTaskRef.current) {
            sendCancelTaskRef.current = sendCancelTaskFn;
        }
    }, [sendCancelTaskFn, sendCancelTaskRef]);

    useSocketEventCallback(socket, SocketEvent.TaskFailed, onTaskFailed);
    useSocketEventCallback(socket, SocketEvent.TaskCompleted, onTaskCompleted);
    useSocketEventCallback(socket, SocketEvent.TaskProgress, onTaskProgress);

    const startTask = useCallback(async (data) => {
        onToggleLoading?.(true);
        onProgress?.(0);
        if (notificationEnabled) {
            notifyProgressStart();
        }

        const newTaskRequest = new TaskRequest(fetchData, onRequestErrorRef.current);
        await newTaskRequest.execute(socket.id, data);
        setTaskRequest((prev) => {
            if (prev?.inProgress) {
                prev.complete();
                sendCancelTaskRef.current(prev.taskId);
            }
            return newTaskRequest;
        });
    }, [notificationEnabled, onToggleLoading, onProgress, setTaskRequest, fetchData, onRequestErrorRef, socket, notifyProgressStart]);

    useEffect(() => {
        const result = taskCompletionResult?.[taskRequest?.taskId];
        if (result && taskRequest?.inProgress) {
            taskRequest.complete();
            onToggleLoading?.(false);
            if (result.status === TaskCompletionStatus.Success) {
                onComplete(result.data);
            } else if (result.status === TaskCompletionStatus.Failed) {
                onError?.(result.error);
            }
        }
    }, [taskCompletionResult, taskRequest, onComplete, onError, onToggleLoading]);

    useEffect(() => () => {
        if (taskRequest?.inProgress) {
            sendCancelTaskRef.current(taskRequest.taskId);
        }
    }, [taskRequest, sendCancelTaskRef]);

    useEffect(() => {
        if (stopTaskSignal && taskRequest?.inProgress) {
            onToggleLoading?.(false);
            sendCancelTaskRef.current(taskRequest.taskId);
        }
        setStopTaskSignal(false);
    }, [stopTaskSignal, onToggleLoading, onError, taskRequest, sendCancelTaskRef]);

    return {startTask: (socket && socket.connected && socket.id ? startTask : null)};
}
